import { Menu } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Outlet, useMatches, useRouterState } from '@tanstack/react-router'
import { Sidebar } from '@/components/layout/Sidebar'
import { Topbar } from '@/components/layout/Topbar'
import { ApiStatusBanner } from '@/components/feedback/ApiStatusBanner'
import { DesktopOnlyBanner } from '@/components/layout/DesktopOnlyBanner'
import { IosInstallBanner } from '@/components/layout/IosInstallBanner'
import { Logo } from '@/components/ds/Logo'
import { JobsWatcher } from '@/features/jobs/components/JobsWatcher'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { cn } from '@/lib/cn'
import styles from './AppLayout.module.scss'
import { projectsLinkSearch } from '@/features/projects/filters'

/**
 * Routes whose UX is desktop-tuned (factory forms + tools). On mobile we
 * still render the page but show a banner up top warning the user that
 * fields may be cramped — they're expected to use desktop for these
 * flows. Match by pathname prefix so nested factory routes
 * (``/factory/viral-shorts``, etc.) all qualify.
 */
const DESKTOP_TUNED_PREFIXES = ['/factory', '/tools'] as const

function isDesktopTuned(pathname: string): boolean {
  return DESKTOP_TUNED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

export function AppLayout() {
  const matches = useMatches()
  const currentMatch = matches[matches.length - 1]
  const isMobile = useIsMobile()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  // Close the drawer on every route change so navigating never leaves it stuck open.
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  // Auto-close when crossing the mobile→desktop threshold (e.g. user
  // resizes the browser): the desktop layout has the sidebar fixed, so
  // ``drawerOpen`` becomes meaningless and the body-scroll lock would
  // otherwise stay on.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  // Lock body scroll while the drawer is open so the page underneath
  // doesn't scroll behind the panel. Restored on close / unmount.
  useEffect(() => {
    if (!drawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  if (isMobile) {
    return (
      <div className={styles.app}>
        <JobsWatcher />

        <header className={styles.mobileHeader}>
          <button
            type="button"
            className={styles.hamburger}
            aria-label="Abrir menu"
            aria-expanded={drawerOpen}
            onClick={() => {
              setDrawerOpen(true)
            }}
          >
            <Menu aria-hidden />
          </button>
          <Link
            to="/"
            search={projectsLinkSearch(null)}
            className={styles.mobileLogo}
            aria-label="Prism · home"
          >
            <Logo variant="full" size="md" />
          </Link>
        </header>

        {drawerOpen ? (
          <button
            type="button"
            className={styles.drawerBackdrop}
            onClick={() => {
              setDrawerOpen(false)
            }}
            aria-label="Fechar menu"
          />
        ) : null}

        <div
          className={cn(styles.drawer, drawerOpen && styles.drawerOpen)}
          role="dialog"
          aria-modal={drawerOpen ? 'true' : undefined}
          aria-hidden={!drawerOpen}
        >
          <Sidebar
            variant="drawer"
            onNavigate={() => {
              setDrawerOpen(false)
            }}
          />
        </div>

        <main className={styles.main}>
          <div className={styles.content}>
            <ApiStatusBanner />
            {isDesktopTuned(pathname) ? <DesktopOnlyBanner /> : null}
            <Outlet />
          </div>
        </main>
        <IosInstallBanner />
      </div>
    )
  }

  return (
    <div className={styles.app}>
      <JobsWatcher />
      <Sidebar variant="fixed" />
      <main className={styles.main}>
        {/* routeId, not id: a match's id also carries the interpolated path
            (``/_app/settings/settings``), which none of the breadcrumb rules match. */}
        <Topbar matchId={currentMatch?.routeId ?? '/'} />
        <div className={styles.content}>
          <ApiStatusBanner />
          <Outlet />
        </div>
      </main>
    </div>
  )
}
